import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

/** Material UI Packages. */
import { Box, Button, CircularProgress, FormControlLabel, Paper, Switch, Typography } from '@mui/material';
import { GridToolbarQuickFilter } from '@mui/x-data-grid-premium';

/** Material UI Icons. */
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import { setRateManagementData } from 'shared/store/slices/rateManagementSlice';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import { MainDatagrid } from 'shared/ui/Datagrid';
import ThemeButton from 'shared/ui/Button';
import SocketAlert from 'shared/ui/SocketAlert';
import CustomSnackbar from 'shared/ui/CustomSnackbar';
import { PageHeader } from 'shared/ui/HeaderIcon';
import AppliedFilterChips from 'shared/ui/AppliedFilterChips';
import { readSocketData } from 'shared/lib/fetchSocket';
import { formatDateWindowText, optionText } from 'shared/lib/filterLabel';

import RateManagementDetailPanel from './components/RateManagementDetailPanel';
import RateManagementFilterBar, { dateFieldLabel } from './components/RateManagementFilterBar';
import { getRateManagementGridColumns } from './lib/rateManagementGridColumns';
import {
    buildRateSearchCriteria,
    clientMultiSelectConflict,
    defaultRateSearchDateWindow,
    isDefaultRateSearchDateWindow,
    rateSearchDateError,
} from './lib/rateManagementSearchCriteria';
import { attachRateManagementQuickSearchField, rateManagementDataGridFontSx } from './lib/rateManagementShared';

/** `readSocketData` 의 tab 인자 — `il_rate_management_shipment_search` 는 백엔드에서 tab 을 쓰지 않음. */
const RATE_MANAGEMENT_TAB = 0;

/** Single-value filters that get a removable chip above the results, in bar order. */
const CHIP_FILTERS = [
    ['shipper', 'Shipper'],
    ['consignee', 'Consignee'],
    ['pol', 'POL'],
    ['aol', 'AOL'],
    ['fpod', 'F.DEST'],
    ['deliveryLocation', 'Delivery Location'],
];

/**
 * `MainDatagrid` 툴바 전용 — QuickFilter만 남기고 Columns/Filters/Density/Export 버튼은 제거,
 * 그 자리에 "Expand all detail panels" 스위치를 넣어 공간을 확보한다.
 * `slotProps.toolbar`로 상태를 주입받으므로 컴포넌트 자체는 모듈 스코프에 고정해 리렌더 시 재마운트되지 않게 한다.
 */
function RateManagementGridToolbar({ allDetailPanelsExpanded, hasRows, disabled, onToggleExpandAll }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                width: '100%',
                px: 1,
                py: 0.5,
            }}
        >
            <FormControlLabel
                control={
                    <Switch
                        checked={allDetailPanelsExpanded}
                        disabled={!hasRows || disabled}
                        onChange={(e) => onToggleExpandAll(e.target.checked)}
                        size="small"
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#00647e' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#00647e',
                            },
                        }}
                    />
                }
                label="Expand all detail panels"
                sx={{
                    mr: 0,
                    flexShrink: 0,
                    userSelect: 'none',
                    '& .MuiFormControlLabel-label': { fontSize: '12px', color: '#003a52', fontWeight: 500 },
                }}
            />

            <GridToolbarQuickFilter sx={{ width: { xs: '100%', sm: 260 } }} />
        </Box>
    );
}

export default function RateManagement() {
    const dispatch = useDispatch();
    const socketConnected = useSelector((state) => state.socketStatus.socketStatus);

    /** Several clients can be searched at once, unless a shipper/consignee is set. */
    const [clients, setClients] = useState([]);
    const [team, setTeam] = useState(null);
    const [shipper, setShipper] = useState(null);
    const [consignee, setConsignee] = useState(null);
    const [pol, setPol] = useState(null);
    const [aol, setAol] = useState(null);
    const [fpod, setFpod] = useState(null);
    const [deliveryLocation, setDeliveryLocation] = useState('');
    /** Several rate types can be searched at once; a shipment matches any of them. */
    const [rateTypes, setRateTypes] = useState([]);
    /** Search period: only shipments whose ETD (or ETA) falls in it are shown. Starts on the past year. */
    const [dateWindow, setDateWindow] = useState(defaultRateSearchDateWindow);
    const [clientError, setClientError] = useState('');
    const gridRows = useSelector((state) => state.rateManagementData);
    const [searchLoading, setSearchLoading] = useState(false);
    const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState([]);

    useEffect(() => {
        document.title = 'Rate Management | Operation System';
    }, []);

    /** Puts every filter back to its starting value; the results on screen stay. */
    const resetFilters = useCallback(() => {
        setClients([]);
        setTeam(null);
        setShipper(null);
        setConsignee(null);
        setPol(null);
        setAol(null);
        setFpod(null);
        setDeliveryLocation('');
        setRateTypes([]);
        setDateWindow(defaultRateSearchDateWindow());
        setClientError('');
    }, []);

    /** The Clear button empties both the filters and the results. */
    const handleClear = useCallback(() => {
        resetFilters();
        dispatch(setRateManagementData([]));
        setDetailPanelExpandedRowIds([]);
    }, [dispatch, resetFilters]);

    /** Current filter values, shared by the filter bar and the chips above the results. */
    const filters = {
        dateWindow,
        clients,
        team,
        shipper,
        consignee,
        pol,
        aol,
        fpod,
        deliveryLocation,
        rateTypes,
    };
    const setters = {
        dateWindow: setDateWindow,
        clients: setClients,
        team: setTeam,
        shipper: setShipper,
        consignee: setConsignee,
        pol: setPol,
        aol: setAol,
        fpod: setFpod,
        deliveryLocation: setDeliveryLocation,
        rateTypes: setRateTypes,
    };

    /** A filter was set; results change only when SEARCH is pressed. */
    const handleFilterChange = (key, value) => {
        setters[key](value);
        const applied = key === 'clients' ? value.length > 0 : key === 'team' && value?.id;
        if (applied) setClientError('');
    };

    /** One chip per set filter. The starting period gets none: it always applies and cannot be removed. */
    const appliedChips = [
        ...(isDefaultRateSearchDateWindow(dateWindow)
            ? []
            : [
                  {
                      key: 'dateWindow',
                      label: dateFieldLabel(dateWindow.field),
                      text: formatDateWindowText(dateWindow.start, dateWindow.end) || 'past year',
                  },
              ]),
        ...(team ? [{ key: 'team', label: 'Team', text: optionText(team) }] : []),
        ...clients.map((c) => ({ key: `client:${c.id}`, label: 'Client', text: optionText(c) })),
        ...CHIP_FILTERS.filter(([key]) => filters[key]).map(([key, label]) => ({
            key,
            label,
            text: optionText(filters[key]),
        })),
        ...rateTypes.map((t) => ({ key: `rateType:${t.id}`, label: 'Rate Type', text: optionText(t) })),
    ];

    /** Removing a chip clears that filter only; the search period goes back to the past year. */
    const handleChipRemove = (key) => {
        if (key === 'dateWindow') return setDateWindow(defaultRateSearchDateWindow());
        if (key.startsWith('client:')) {
            const id = key.slice('client:'.length);
            return setClients((prev) => prev.filter((c) => String(c.id) !== id));
        }
        if (key.startsWith('rateType:')) {
            const id = key.slice('rateType:'.length);
            return setRateTypes((prev) => prev.filter((t) => String(t.id) !== id));
        }
        return setters[key](key === 'deliveryLocation' ? '' : null);
    };

    const handleSearch = useCallback(() => {
        if (clients.length === 0 && !team?.id) {
            setClientError('Client or Team is required.');
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'Please select a client or a team before searching.',
                    severity: 'warning',
                })
            );
            return;
        }
        setClientError('');
        const conflict = clientMultiSelectConflict({ clients, shipper, consignee });
        if (conflict) {
            dispatch(setSnackbar({ open: true, message: conflict, severity: 'warning' }));
            return;
        }
        if (pol?.id != null && aol?.id != null) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'POL and AOL cannot both be selected. Clear one before searching.',
                    severity: 'warning',
                })
            );
            return;
        }
        const dateError = rateSearchDateError(dateWindow);
        if (dateError) {
            dispatch(setSnackbar({ open: true, message: dateError, severity: 'warning' }));
            return;
        }
        if (!socketConnected) return;

        const criteria = buildRateSearchCriteria({
            clients,
            team,
            shipper,
            consignee,
            pol,
            aol,
            fpod,
            deliveryLocation,
            rateTypes,
            dateWindow,
        });
        setSearchLoading(true);
        readSocketData(
            dispatch,
            RATE_MANAGEMENT_TAB,
            'row',
            'il_rate_management_shipment_search',
            '',
            criteria,
            (data) => {
                const rows = Array.isArray(data) ? data : [];
                dispatch(
                    setRateManagementData(
                        rows.map((row, i) =>
                            attachRateManagementQuickSearchField({
                                ...row,
                                id: row.id ?? row.il_ocean_import_id ?? `rate-mgmt-${i}`,
                            })
                        )
                    )
                );
                // 새 검색 결과가 들어오면 detail panel은 기본적으로 닫힌 상태로 유지한다.
                setDetailPanelExpandedRowIds([]);
            },
            () => {},
            () => setSearchLoading(false)
        );
    }, [
        clients,
        team,
        shipper,
        consignee,
        dateWindow,
        pol,
        aol,
        fpod,
        deliveryLocation,
        rateTypes,
        dispatch,
        socketConnected,
    ]);

    const columns = useMemo(() => getRateManagementGridColumns(), []);
    const allDetailPanelsExpanded = useMemo(() => {
        if (!gridRows.length) return false;
        const expanded = new Set(detailPanelExpandedRowIds);
        return gridRows.every((r) => expanded.has(r.id));
    }, [gridRows, detailPanelExpandedRowIds]);

    const getRowHeight = useCallback(() => 'auto', []);
    const getDetailPanelHeight = useCallback(() => 'auto', []);
    const getDetailPanelContent = useCallback(({ row }) => {
        return <RateManagementDetailPanel row={row} />;
    }, []);

    return (
        <>
            <SocketAlert />
            <CustomSnackbar />
            <Paper sx={{ ...customized_style.itemPaper, pt: 2 }}>
                <PageHeader title="RATE MANAGEMENT" icon={AttachMoneyIcon} sx={{ mb: 1 }} />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'center' },
                        justifyContent: 'space-between',
                        gap: 1,
                        width: '100%',
                        p: 1.25,
                        pl: '12px',
                        mb: 1,
                        borderRadius: 2,
                        border: '1px solid rgba(0, 100, 126, 0.14)',
                        bgcolor: 'rgba(0, 100, 126, 0.035)',
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                        <RateManagementFilterBar
                            filters={filters}
                            onChange={handleFilterChange}
                            dispatch={dispatch}
                            requiredMissing={Boolean(clientError)}
                        />
                        {clientError && (
                            <Typography sx={{ fontSize: 11, color: '#d32f2f', pl: 0.5 }}>{clientError}</Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClear}
                            disabled={searchLoading}
                            startIcon={<ClearIcon sx={{ fontSize: '16px !important' }} />}
                            sx={{
                                width: '92px',
                                height: '26px',
                                fontSize: '12px',
                                color: '#00647e',
                                borderColor: '#00647e',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 1.25,
                                '&:hover': { borderColor: '#003a52', bgcolor: 'rgba(0, 100, 126, 0.06)' },
                            }}
                        >
                            Clear
                        </Button>
                        <ThemeButton
                            text="Search"
                            icon={<SearchIcon />}
                            width="92px"
                            onClick={handleSearch}
                            disabled={!socketConnected || searchLoading}
                            sx={{
                                height: '26px',
                                fontSize: '12px',
                                minHeight: 26,
                                borderRadius: '6px',
                                boxShadow: 'none',
                            }}
                        />
                    </Box>
                </Box>
                <AppliedFilterChips filters={appliedChips} onRemove={handleChipRemove} onClearAll={resetFilters} />
                <Box sx={{ position: 'relative', flex: 1, mt: 0, height: '77.5vh', width: '100%', minHeight: 0 }}>
                    <MainDatagrid
                        rows={gridRows}
                        cols={columns}
                        showToolbar="CustomToolbar"
                        loading={false}
                        id_status={false}
                        hide_status={{ _rate_search: false, _quickSearch_: false }}
                        additional={{
                            isCellEditable: () => false,
                            disableColumnReorder: true,
                            showCellVerticalBorder: true,
                            getRowHeight,
                            getDetailPanelHeight,
                            getDetailPanelContent,
                            detailPanelExpandedRowIds,
                            onDetailPanelExpandedRowIdsChange: setDetailPanelExpandedRowIds,
                            slots: { toolbar: RateManagementGridToolbar },
                            slotProps: {
                                toolbar: {
                                    allDetailPanelsExpanded,
                                    hasRows: gridRows.length > 0,
                                    disabled: searchLoading,
                                    onToggleExpandAll: (checked) => {
                                        setDetailPanelExpandedRowIds(checked ? gridRows.map((r) => r.id) : []);
                                    },
                                },
                            },
                            initialState: {
                                filter: {
                                    filterModel: { quickFilterExcludeHiddenColumns: false },
                                },
                            },
                        }}
                        sx={{
                            height: '100%',
                            width: '100%',
                            ...rateManagementDataGridFontSx,
                        }}
                    />
                    {searchLoading ? (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(255,255,255,0.72)',
                                zIndex: 10,
                            }}
                            aria-busy="true"
                            aria-label="Loading search results"
                        >
                            <CircularProgress size={48} thickness={4} sx={{ color: 'primary.main' }} />
                        </Box>
                    ) : null}
                </Box>
            </Paper>
        </>
    );
}

export { GooglePlacesDeliveryField } from './components/GooglePlacesDeliveryField';
export {
    TradePartnerAutocomplete,
    LocationCodeNameAutocomplete,
    RateTypeAutocomplete,
    TeamAutocomplete,
} from './components/RateManagementAutocompletes';
export { formatPartnerNames, formatLocation, formatMultiLineField, formatEtdEta } from './lib/rateManagementFormatters';
